import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { StatusTag } from '@/components/Tag/StatusTag';

export const RepairCostingTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={6}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/repair-costing/create')}
        />
      )}
      {data?.map((entry) => {
        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            <TableCell
              tdClassName="sticky-select-column"
              content={
                <Checkbox
                  checked={Boolean(
                    selectedTableData?.find((item) => item?.id === entry?.id),
                  )}
                  onChange={() => onSelectedItem(entry?.id)}
                />
              }
            />
            <TableCell
              content={
                <div className="flex items-center gap-1">
                  <div>{entry?.equipment?.name}</div>
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex items-center gap-1">
                  <div>{entry?.damage?.name}</div>
                  <div className="text-gray-78819D/80 text-xs">
                    &nbsp;&#8212;&nbsp;
                  </div>
                  <div className="text-gray-78819D/80 font-medium">{`${t('common:level')} : ${entry?.damage?.severity}`}</div>
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex items-center gap-1">
                  <div>{entry?.repairService?.name}</div>
                </div>
              }
            />
            <TableCell
              content={
                <StatusTag
                  text={`${entry?.cost} €`}
                  className="text-left px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                />
              }
            />
            <TableCell
              content={
                <StatusTag
                  text={`${
                    entry?.active ? t('common:active') : t('common:inactive')
                  }`}
                  className={
                    entry?.active
                      ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                      : 'bg-red-FFEEF4 border-red-FFCAD6 text-red-F8275A text-xs dark-mode:bg-red-FFEEF4/10 dark-mode:border-red-FFCAD6/10 dark-mode:text-red-F8275A/80 dark-mode:tracking-wider'
                  }
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={`/repair-costing/edit/${entry?.id}`}
                  />
                  <SubMenuItem
                    text={t('common:deactivate')}
                    disabled={!entry?.active || pendingDeactivate}
                    leftIconClassName="ki-trash !text-[19px]"
                    textClassName="line-clamp-1"
                    onClick={() => onDeactivate([entry])}
                  />
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
